import { useEffect, useRef, useState } from 'react';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { t } from '../services/language-manager';
import { registerResumeDraft, resumeDraft } from '../services/reauth-resume';
import { saveOrgSettings } from '../services/wizard-backend';
import { SaveButton, SaveRow, useSaveConfirmation } from './SaveFeedback';

interface HistoryTagSearchSectionProps {
  genesysOrgId: string;
  genesysRegion: string;
  /** Stored value. Missing on the organization reads as off. */
  enabled: boolean;
  /** Dev preview: keep the payload on the section instead of calling the API. */
  preview?: boolean;
}

/**
 * The agent's Other Conversations list also includes conversations with
 * the same external tag. Off by default: each lookup costs extra Genesys calls.
 * Saving sends only `historyTagSearchEnabled` on the organization settings route.
 */
export function HistoryTagSearchSection({
  genesysOrgId,
  genesysRegion,
  enabled,
  preview = false,
}: HistoryTagSearchSectionProps) {
  // Edits kept when the session expired come back unsaved.
  const [checked, setChecked] = useState(() => {
    const draft = resumeDraft<unknown>('settings-history-tag');
    return typeof draft === 'boolean' ? draft : enabled;
  });
  const [persisted, setPersisted] = useState(enabled);
  const kept = useRef({ checked, persisted });
  kept.current = { checked, persisted };

  useEffect(() => registerResumeDraft('settings-history-tag', () => (
    kept.current.checked === kept.current.persisted ? null : kept.current.checked
  )), []);
  const [saving, setSaving] = useState(false);
  const saveConfirmation = useSaveConfirmation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedPayload, setSavedPayload] = useState<string | null>(null);

  const handleSave = async () => {
    const body = { historyTagSearchEnabled: checked };
    setSaving(true);
    saveConfirmation.clear();
    setSaveError(null);
    try {
      if (preview) {
        setPersisted(checked);
        setSavedPayload(JSON.stringify(body));
        saveConfirmation.confirm(t('settings-save-success'));
        return;
      }
      const result = await saveOrgSettings(genesysOrgId, genesysRegion, body);
      if (result.status) {
        setPersisted(checked);
        saveConfirmation.confirm(t('settings-save-success'));
        return;
      }
      setSaveError(serverErrorMessage({
        code: result.code,
        cause: result.cause,
        knownKey: result.code ? undefined : 'settings-save-error',
        keepDetail: true,
      }));
    } catch (e) {
      setSaveError(caughtErrorMessage(e, 'settings-save-error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      id="history-tag-search"
      className="settings-section"
      {...(preview && savedPayload ? { 'data-saved-history-tag-search': savedPayload } : {})}
    >
      <h3 className="settings-section__title">{t('settings-history-title')}</h3>
      <div className="form-group">
        <label className="settings-page__toggle">
          <input
            type="checkbox"
            checked={checked}
            aria-describedby="history-tag-search-help"
            onChange={(e) => {
              setChecked(e.target.checked);
              saveConfirmation.clear();
              setSaveError(null);
            }}
          />
          <span>{t('settings-history-tag-enable')}</span>
        </label>
        <p className="form-hint" id="history-tag-search-help">{t('settings-history-tag-help')}</p>
      </div>

      <SaveRow className="settings-section__save" error={saveError} message={saveConfirmation.message}>
        <SaveButton
          saving={saving}
          justSaved={saveConfirmation.justSaved}
          label={t('settings-save')}
          savingLabel={t('settings-saving')}
          onClick={handleSave}
          disabled={checked === persisted}
        />
      </SaveRow>
    </section>
  );
}
